import { useState, useMemo } from 'react'
import { ShieldAlert, ShieldCheck, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'

interface MfaGracePeriodProps {
  gracePeriodStart: string
  loginCount: number
  maxLogins: number
  maxDays: number
  onSetupMfa: () => void
}

export function MfaGracePeriod({
  gracePeriodStart,
  loginCount,
  maxLogins,
  maxDays,
  onSetupMfa,
}: MfaGracePeriodProps) {
  const [dismissed, setDismissed] = useState(false)

  const { daysRemaining, loginsRemaining, isExpired, isUrgent } = useMemo(() => {
    const start = new Date(gracePeriodStart)
    const now = new Date()
    const elapsedDays = Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
    const days = Math.max(0, maxDays - elapsedDays)
    const logins = Math.max(0, maxLogins - loginCount)
    const expired = days <= 0 || logins <= 0
    const urgent = !expired && (days <= 7 || logins <= 1)

    return { daysRemaining: days, loginsRemaining: logins, isExpired: expired, isUrgent: urgent }
  }, [gracePeriodStart, loginCount, maxLogins, maxDays])

  // Blocking modal when grace period has expired — no close/dismiss allowed
  if (isExpired) {
    return (
      <Modal open onClose={() => {}} size="sm">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">MFA Setup Required</h2>

          <div className="flex items-center gap-3 rounded-lg bg-red-50 p-4">
            <ShieldAlert className="h-6 w-6 shrink-0 text-red-600" />
            <div>
              <p className="text-sm font-semibold text-red-800">
                Your MFA grace period has expired
              </p>
              <p className="mt-1 text-sm text-red-700">
                To continue using your account, you must enable multi-factor authentication. This
                protects your account from unauthorised access.
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h4 className="text-sm font-semibold text-gray-900">What is MFA?</h4>
            <p className="mt-1 text-xs text-gray-600">
              Multi-factor authentication adds an extra layer of security by requiring a
              verification code from your phone each time you sign in, in addition to your password.
            </p>
          </div>

          <Button onClick={onSetupMfa} className="w-full">
            <ShieldCheck className="mr-1.5 h-4 w-4" />
            Set Up MFA Now
          </Button>
        </div>
      </Modal>
    )
  }

  // Banner dismissed by user (non-urgent only)
  if (dismissed && !isUrgent) return null

  // Warning banner
  const bannerClasses = isUrgent
    ? 'border-red-300 bg-red-50 text-red-800'
    : 'border-yellow-300 bg-yellow-50 text-yellow-800'

  const iconClasses = isUrgent ? 'text-red-600' : 'text-yellow-600'

  return (
    <div
      className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${bannerClasses}`}
      role="alert"
    >
      <ShieldAlert className={`h-5 w-5 shrink-0 ${iconClasses}`} />

      <div className="flex-1 text-sm">
        {isUrgent ? (
          <span className="font-semibold">
            Urgent: Enable MFA within {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} (
            {loginsRemaining} {loginsRemaining === 1 ? 'login' : 'logins'} remaining) to keep your
            account secure
          </span>
        ) : (
          <span>
            Enable MFA within {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} (
            {loginsRemaining} {loginsRemaining === 1 ? 'login' : 'logins'} remaining) to keep your
            account secure
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={onSetupMfa}>
          <ShieldCheck className="mr-1.5 h-4 w-4" />
          Set Up MFA
        </Button>

        {!isUrgent && (
          <button
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            className="rounded-lg p-1 text-current opacity-60 hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}
